import { Color } from '@coast/schemas';
import { ClassValue } from 'clsx';
import { isEmpty } from 'lodash';
import { Tag } from '@shared/components/Tag';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { TagField } from '@shared/data/entity/fields/TagField';
import { Colors } from '@shared/styles';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { getTagText } from './util/getTagText';
import styles from './TagCollectionViewField.module.css';

const MAX_TAGS_TO_SHOW = 3;

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: TagField;
}

/**
 * Default: up to three tags and a "+N". Compact: the first tag and a "+N" for the rest.
 * Inline: the first tag's label and a "+N".
 */
export function TagCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.tagCollectionViewField, className]}
    >
      {isInline && <InlineTag field={field} />}
      {isCompact && <CompactTag field={field} />}
      {isDefault && <DefaultTag field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineTag({ field }: { field: TagField }) {
  return <>{getTagText(field)}</>;
}

function DefaultTag({ field }: { field: TagField }) {
  const visibleTagValues = field.tagIds.slice(0, MAX_TAGS_TO_SHOW);
  const remainingTagCount = field.tagIds.length - visibleTagValues.length;
  const visibleTags = field.tagOptions.filter(tag => visibleTagValues.includes(tag.value));
  return (
    <>
      {visibleTags.map(tag => (
        <Tag
          key={tag.value}
          color={tag.backgroundColor as Color}
          archived={!!tag.archived}
          label={tag.label}
        />
      ))}
      {remainingTagCount > 0 && (
        <Pill
          round
          noBorder
          backgroundColor={Colors.v2.transparent as Color}
          sections={[`+${remainingTagCount}`]}
        />
      )}
    </>
  );
}

/** The first tag, as typed when it isn't one of the field's options, and a "+N" for the rest. */
function CompactTag({ field }: { field: TagField }) {
  const [firstTagId, ...moreTagIds] = field.tagIds;
  const option = field.tagOptions.find(tagOption => tagOption.value === firstTagId);
  return (
    <View row centerVertically className={styles.tags}>
      <Tag
        label={option?.label ?? firstTagId}
        color={option?.backgroundColor as Color | undefined}
        archived={!!option?.archived}
      />
      {!isEmpty(moreTagIds) && <Text weight="normal">{`+${moreTagIds.length}`}</Text>}
    </View>
  );
}
